import { useContext, useEffect } from "react";
import { useFetch, invalidate } from "./useFetch.js";
import { useRepo } from "./repo.js";
import { runQuery } from "../api.js";
import { EmbedIdContext, EmbedReportContext } from "./embedId.js";
import type { QueryResult } from "../../shared/types.js";

// Stale pickup: a `stale: true` result means the server served an old cache
// and kicked a background refresh. Refetch shortly after (without `force` —
// by then the refresh has landed and the cache is fresh). Bounded backoff so
// a refresh that keeps failing can't loop forever.
const stalePickup = new Set<string>();
const staleRetries = new Map<string, number>();
function scheduleStalePickup(key: string): void {
  const n = staleRetries.get(key) ?? 0;
  if (n >= 3) return;
  staleRetries.set(key, n + 1);
  setTimeout(() => {
    stalePickup.add(key);
    invalidate(key);
  }, 1500 * 2 ** n);
}

// Client-side concurrency limit: a page full of embeds shouldn't fire all its
// queries at once. At most MAX_CONCURRENT run in parallel; the rest queue and
// start as slots free up. (Pairs with the server throttle.)
const MAX_CONCURRENT = 4;
let active = 0;
const queue: (() => void)[] = [];
function acquire(): Promise<void> {
  if (active < MAX_CONCURRENT) {
    active++;
    return Promise.resolve();
  }
  return new Promise<void>((res) => queue.push(res));
}
function release(): void {
  const next = queue.shift();
  if (next) next(); // hand the slot to the next waiter
  else active--; // no waiter: free it
}
async function gate<T>(fn: () => Promise<T>): Promise<T> {
  await acquire();
  try {
    return await fn();
  } finally {
    release();
  }
}

/**
 * Shared query runner. Multiple viz blocks with the same SQL (+ params + path)
 * hit the cache once per page; pass `force` (or call refetch) to re-run. Pass a
 * null sql to stand down (e.g. a query blocked on an unfilled parameter).
 */
export function useQuery(
  sql: string | null,
  opts: {
    ttl?: string;
    /** Parameter values to bind, keyed by name. */
    values?: Record<string, string>;
    /** Page path, for @ref include resolution. */
    path?: string | null;
  } = {},
) {
  // The embed's DOM id (from EmbedFrame), so the live task can scroll back to it.
  const embedId = useContext(EmbedIdContext);
  const report = useContext(EmbedReportContext);
  const repo = useRepo();
  const valuesKey = JSON.stringify(opts.values ?? {});
  const key = sql
    ? `query:${repo}:${opts.ttl ?? ""}:${opts.path ?? ""}:${valuesKey}:${sql}`
    : null;
  const state = useFetch<QueryResult>(key, ({ refetched }) =>
    gate(() =>
      runQuery(repo, sql!, {
        ttl: opts.ttl,
        // No explicit ttl and no `@maxAge` pragma → the server's configured embed
        // default window (`datawiki-config/cache` defaultMaxAge). "default" is the
        // sentinel the /query route maps to that config (see docs/sql/Caching.md).
        maxAgeDefault: "default",
        // A stale-pickup refetch must NOT force — the point is to read the
        // cache the background refresh just wrote, not to re-run the query.
        force: refetched && !(key && stalePickup.delete(key)),
        values: opts.values,
        path: opts.path ?? undefined,
        embedId: embedId || undefined,
      }),
    ).then((r) => {
      if (key) {
        if (r.stale) scheduleStalePickup(key);
        else staleRetries.delete(key);
      }
      return r;
    }),
  );
  // Report the result's freshness to the embed frame (the data-age hover title).
  // Runs on cache hits too — not only fetches — so a navigated-back embed still
  // shows its age.
  const data = state.status === "ready" ? state.data : null;
  useEffect(() => {
    report(
      data ? { cachedAt: data.cachedAt, source: data.source, stale: data.stale } : null,
    );
  }, [data, report]);

  // Age-aware revalidation. A client-cache hit never touches the server, so a
  // warm entry (a tab left open, or navigated back to) can age past its window
  // unnoticed — useFetch just re-serves it. When the result has aged past the
  // window the server applied (`maxAgeMs`), revalidate through the capped,
  // non-force stale-pickup: it reads a fresher server cache, or trips the
  // server's own SWR refresh if that's stale too.
  useEffect(() => {
    if (!key || !data?.cachedAt) return;
    const window = data.maxAgeMs;
    if (typeof window !== "number" || !Number.isFinite(window) || window <= 0) return;
    const age = Date.now() - new Date(data.cachedAt).getTime();
    if (age > window) scheduleStalePickup(key);
  }, [key, data]);
  return state;
}
